import React, { useCallback, useEffect } from 'react'
import { FormFields } from '~/components/FormFields'
import { Controller, useFieldArray, useFormContext, useWatch } from 'react-hook-form'
import { Checkbox, Form, Grid, Icon, Input, Popup } from 'semantic-ui-react'
import { CheckClimaEl } from '~/pages/Farmer/Co2Climate/style'
import { formatNumber, splitEvery } from '~/utils/helpers'
import InfoPopup from '~/components/InfoPopup'

interface Props {
  housingTypes: any[]
}

const SixthStep: React.FC<Props> = ({ housingTypes }) => {
  const { control, getValues, formState: { isSubmitted, errors }, clearErrors } = useFormContext()
  const watchCowsNumber = useWatch({ control, name: 'youngCattleNumber' })
  const transformedCowsNumber = watchCowsNumber?.toString()?.replace(',', '.') || 0

  const { fields, append, remove, update } = useFieldArray({
    control,
    name: 'calvesHousingTypes',
    keyName: 'key'
  })

  const fieldNames = [
    'youngCattlePastureGroup',
    'youngCattlePastureDays',
    'youngCattlePastureDuration'
  ]

  const [
    watchYoungPastureGroup,
    watchYoungPastureDays,
    watchYoungPastureDuration
  ] = useWatch({ control, name: fieldNames }) || []

  useEffect(() => {
    const hasError = Boolean(Object.keys(errors).find(errorKey => fieldNames.find(name => name === errorKey)))
    const emptyFields = !(Boolean(watchYoungPastureDays) || Boolean(watchYoungPastureDuration) || Boolean(watchYoungPastureGroup))
    if (hasError && emptyFields) {
      clearErrors(fieldNames)
    }
  }, [Object.keys(errors).length, watchYoungPastureDays, watchYoungPastureDuration, watchYoungPastureGroup])

  function handleChecked(field, checked) {
    if (checked) {
      append({
        ...field,
        cattleNumber: ''
      })
      return
    }
    const index = fields.findIndex(fieldManure => fieldManure['id'] === field.id)
    if (index >= 0) {
      remove(index)
    }
  }

  function getTotalEnteredCows() {
    return fields.reduce((previousValue, currentValue) => {
      return previousValue + parseInt(currentValue['cattleNumber']?.toString()?.replace(',', '.') || 0)
    }, 0)
  }

  function checkCowsTotal() {
    const cowsEnteredTotal = getTotalEnteredCows()
    return cowsEnteredTotal != transformedCowsNumber
  }

  const renderLabel = useCallback(() => {
    const cowsEnteredTotal = getTotalEnteredCows()
    const hasErrors = isSubmitted && cowsEnteredTotal != transformedCowsNumber
    return (
      <label
        style={{
          fontSize: '15px',
          color: hasErrors ? 'red' : 'initial'
        }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <div>
            {`Färse [${cowsEnteredTotal} / ${formatNumber(transformedCowsNumber, 0)}]`}
          </div>
        </div>
      </label>
    );
  }, [fields]);

  const optionsSplit = splitEvery(housingTypes, 4)

  const pastureFieldsRequired = Boolean(watchYoungPastureDays) || Boolean(watchYoungPastureDuration) || Boolean(watchYoungPastureGroup)

  return <div>
    <Form.Field>
      {renderLabel()}
      <CheckClimaEl.StyledDivider />
      <Grid columns={2} style={{ marginTop: '1em' }}>
        <Controller
          name={'calvesHousingTypes'}
          control={control}
          rules={{
            validate: () => !checkCowsTotal()
          }}
          render={({ fieldState }) => {
            const error = fieldState.error ? checkCowsTotal() : false
            return <>
              {optionsSplit.map((optionSplit) => {
                return <Grid.Column key={optionSplit.key}>
                  <Grid columns={2}>
                    {optionSplit.map((option) => {
                      const fieldIndex = fields.findIndex(field => field['id'] === option.id)
                      return <Grid.Row style={{ alignItems: 'center', padding: '0.6rem 0rem' }}
                        key={option.id}
                      >
                        <Grid.Column>
                          <Checkbox
                            label={<label style={{ display: 'flex', gap: 5 }}>
                              <div>{option.label}</div>
                              {option.calvesTooltip && <InfoPopup
                                content={option.calvesTooltip}
                                styleIcon={{ fontSize: '1.2em' }}
                                position={'top center'}
                              />}
                            </label>}
                            checked={Boolean(fields.find(manureField => manureField['id'] === option.id))}
                            onChange={(e, data) => handleChecked(option, data.checked)}
                          />
                        </Grid.Column>
                        <Grid.Column>
                          {fieldIndex >= 0 ? <FormFields.Input
                            control={control}
                            style={{ width: 200 }}
                            error={error}
                            typeInput={'controled'}
                            styleLabel={{ marginBottom: 0 }}
                            suffix={' Färsen'}
                            onBlur={() => {
                              const value = getValues(`calvesHousingTypes[${fieldIndex}].cattleNumber`)
                              update(fieldIndex, {
                                ...fields[fieldIndex],
                                cattleNumber: value
                              })
                            }}
                            noErrorMessage
                            withoutAsterisk
                            rules={{
                              required: true
                            }}
                            name={`calvesHousingTypes[${fieldIndex}].cattleNumber`}
                            placeholder={'Färsen'}
                          /> : <Input
                            style={{ width: 200 }}
                            disabled
                            placeholder={'Färsen'}
                          />
                          }
                        </Grid.Column>
                      </Grid.Row>
                    })}
                  </Grid>
                </Grid.Column>
              })}

            </>
          }}
        />
      </Grid>
    </Form.Field>
    <Grid columns={3}>
      <Grid.Row>
        <Grid.Column>
          <FormFields.Input
            control={control}
            name={'youngCattlePastureGroup'}
            noErrorMessage={!watchYoungPastureGroup}
            suffix={' Färsen'}
            withoutAsterisk
            label={'Weidegruppe'}
            typeInput='controled'
            styleErrorLabel={{ position: 'absolute', zIndex: 999, left: 20, marginTop: '0.5em' }}
            rules={{
              required: pastureFieldsRequired,
              min: 0,
              max: {
                value: parseInt(watchCowsNumber),
                message: `Die Anzahl der Färsen in der Weidegruppe (${watchYoungPastureGroup}) muss kleiner gleich Ihrem  ⌀-Bestand (${watchCowsNumber}) sein.`
              }
            }}
            placeholder={'Anzahl Färsen'}
          />
        </Grid.Column>
        <Grid.Column>
          <FormFields.Input
            control={control}
            name={'youngCattlePastureDays'}
            noErrorMessage={!watchYoungPastureDays}
            suffix={' Tage/Jahr'}
            withoutAsterisk
            label={<div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: '0.5em' }}>
              <label>{'Weidetage'}</label>
              {(watchYoungPastureDays < 40 || watchYoungPastureDays > 240) && <Popup
                content={'Die Anzahl an Weidetagen liegt außerhalb des Normalbereichs von mindestens 40 Tagen und maximal 240 Tagen'}
                trigger={<Icon name='warning sign' color='yellow' style={{ fontSize: '1.2em' }} />}
              />}
            </div>}
            typeInput='controled'
            rules={{
              required: pastureFieldsRequired,
              min: 0,
              max: {
                value: 365,
                message: 'Die maximale Anzahl der Weidetage pro Jahr beträgt 365 Tage.'
              }
            }}
            placeholder={'Tage/Jahr'}
          />
        </Grid.Column>
        <Grid.Column>
          <FormFields.Input
            control={control}
            noErrorMessage={!watchYoungPastureDuration}
            name={'youngCattlePastureDuration'}
            suffix={' Stunden/Tag'}
            withoutAsterisk
            label={'Weidehaltungsdauer'}
            typeInput='controled'
            rules={{
              required: pastureFieldsRequired,
              min: 0,
              max: {
                value: 24,
                message: 'Die maximale Weidehaltungsdauer pro Tag beträgt 24 Stunden'
              }
            }}
            placeholder={'Stunden/Tag'}
          />
        </Grid.Column>
      </Grid.Row>
    </Grid>
  </div>
}
export default SixthStep